import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Intro from "@/components/Intro";
import Hero from "@/components/Hero";
import Dome from "@/components/Dome";
import PhoneMockup from "@/components/PhoneMockup";
import ShareCardButton from "@/components/ShareCard";
import SaathiCards from "@/components/SaathiCards";
import { Reveal, SectionHead, GoldBtn, Marquee, Countdown, StatCell, AddToCalendar, Particles, GlowOrbs, Magnetic } from "@/components/Shared";
import {
    STATS, WHY_CARDS, PILLARS, PAVILIONS, POD_CAPABILITIES, MAHATALKS_TOPICS,
    PITCH_ROUNDS, ADDA_FEATURES, IMPACT_FLOW, IMPACT_FOCUS, MASTERCLASSES,
    DIGITAL_FEATURES, AWARDS, ZONES, EVENINGS, WHO_ATTEND, WHY_ATTEND, EVENT,
} from "@/data/content";

const wrap = "py-24 md:py-36 relative overflow-hidden";
const EASE = [0.22, 1, 0.36, 1];

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09 } } };
const riseItem = { hidden: { opacity: 0, y: 42 }, show: { opacity: 1, y: 0, transition: { duration: 0.95, ease: EASE } } };

const MANIFESTO = [
    { n: "01", word: "Ideas", line: "Here, ideas are not showcased — they're celebrated." },
    { n: "02", word: "Innovation", line: "Because innovation is not just built — it's felt." },
    { n: "03", word: "Capital", line: "Where conversations become collaborations — and collaborations into capital." },
    { n: "04", word: "Collaboration", line: "Here, startups don't compete — they collaborate." },
    { n: "05", word: "Commerce", line: "Where every idea finds its market, and every market finds its maker." },
    { n: "06", word: "Impact", line: "Building Bharat's innovation identity — for 2047 and beyond." },
];

const IsoPods = () => {
    const heights = [0, 20, 8, 26, 12, 4, 24, 10, 30, 6, 16, 2, 22, 12, 8, 26];
    return (
        <div className="flex justify-center" style={{ perspective: "1100px" }} aria-hidden="true">
            <div
                className="grid grid-cols-4 gap-5 w-[320px] sm:w-[430px]"
                style={{ transform: "rotateX(56deg) rotateZ(-42deg)", transformStyle: "preserve-3d" }}
            >
                {heights.map((h, i) => (
                    <div
                        key={i}
                        className="aspect-square border transition-all duration-700 hover:border-[var(--gold)]"
                        style={{
                            borderColor: "rgba(201,162,75,0.35)",
                            background:
                                i % 5 === 0
                                    ? "linear-gradient(135deg, rgba(92,16,28,0.85), rgba(38,9,16,0.95))"
                                    : "linear-gradient(135deg, #250810, #1E060C)",
                            boxShadow: i % 5 === 0 ? "0 0 34px rgba(201,162,75,0.3)" : "0 0 0 rgba(0,0,0,0)",
                            transform: `translateZ(${h}px)`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

const ImpactFlow = () => (
    <div className="flex flex-wrap items-center justify-center gap-y-6" data-testid="impact-flow">
        {IMPACT_FLOW.map((s, i) => (
            <div key={s} className="flex items-center">
                <Reveal delay={i * 120}>
                    <div className="px-6 py-4 border border-[var(--line)] bg-[rgba(38,9,16,0.7)] text-center min-w-[8.5rem]">
                        <span className="block text-[0.55rem] tracking-[0.34em] text-[var(--gold)] uppercase">0{i + 1}</span>
                        <span className="block font-display text-lg text-[var(--ivory)] mt-1">{s}</span>
                    </div>
                </Reveal>
                {i < IMPACT_FLOW.length - 1 && (
                    <span className="hidden md:block w-10 h-px mx-1 shimmer-line" aria-hidden="true" />
                )}
            </div>
        ))}
    </div>
);

const TrophyGlyph = ({ className = "w-40" }) => (
    <svg viewBox="0 0 160 190" fill="none" className={className} aria-hidden="true">
        <path d="M55 30 H105 V78 Q105 104 80 104 Q55 104 55 78 Z" stroke="var(--gold)" strokeWidth="1.6" />
        <path d="M63 38 H97 V76 Q97 96 80 96 Q63 96 63 76 Z" stroke="rgba(201,162,75,0.45)" strokeWidth="0.9" />
        <path d="M55 40 Q30 40 34 62 Q37 80 57 78" stroke="var(--gold)" strokeWidth="1.4" />
        <path d="M105 40 Q130 40 126 62 Q123 80 103 78" stroke="var(--gold)" strokeWidth="1.4" />
        <path d="M80 104 V124" stroke="var(--gold)" strokeWidth="1.6" />
        <path d="M60 138 H100 L94 124 H66 Z" stroke="var(--gold)" strokeWidth="1.4" />
        <path d="M48 160 H112 V138 H48 Z" stroke="var(--gold)" strokeWidth="1.4" />
        <path d="M80 46 L84 56 L94 57 L86 64 L88 74 L80 69 L72 74 L74 64 L66 57 L76 56 Z" fill="rgba(232,207,143,0.85)" />
        <circle cx="80" cy="88" r="2" fill="var(--champagne)" className="pulse-soft" />
    </svg>
);

export { TrophyGlyph };

export default function Home() {
    const [intro, setIntro] = useState(() => !sessionStorage.getItem("mb_intro_seen"));
    const closeIntro = () => {
        sessionStorage.setItem("mb_intro_seen", "1");
        setIntro(false);
    };

    return (
        <div data-testid="home-page">
            <AnimatePresence>{intro && <Intro onDone={closeIntro} />}</AnimatePresence>
            <Hero started={!intro} />

            {/* 02 · DATE / COUNTDOWN / STATS */}
            <section className="relative border-y hairline bg-[#1C060C]/80 noise">
                <div className="container-x py-12 md:py-16">
                    <Reveal className="flex flex-col lg:flex-row items-center justify-between gap-8">
                        <div>
                            <span className="eyebrow no-lines">The Gates Open In</span>
                            <p className="mt-3 text-[0.62rem] tracking-[0.34em] uppercase text-muted-luxe">
                                29–31 January 2027 · MSIT &amp; MSI, New Delhi
                            </p>
                            <div className="mt-4 flex flex-wrap items-center gap-6">
                                <AddToCalendar />
                                <ShareCardButton />
                            </div>
                        </div>
                        <Countdown />
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-[rgba(201,162,75,0.08)] border-t hairline">
                            {STATS.map((s) => (
                                <StatCell key={s.label} value={s.value} suffix={s.suffix} label={s.label} />
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <Marquee
                editorial
                items={[
                    "Bazaar of Ideas", "Innovation", "Impact", "120+ Startups", "11 Pavilions",
                    "PitchYatra", "Investor Adda", "MahaTalks", "Knowledge Labs", "Bharat 2047",
                ]}
            />

            {/* 03 · WELCOME + MANIFESTO */}
            <section className={`${wrap} noise`}>
                <GlowOrbs />
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[36rem] h-[36rem] rounded-full opacity-25"
                        style={{ background: "radial-gradient(circle, rgba(92,16,28,0.85), transparent 65%)" }} />
                </div>
                <div className="container-x relative">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <Reveal>
                            <span className="eyebrow no-lines">01 · Welcome</span>
                            <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.06] mt-6">
                                Welcome to the <span className="gold-text italic">Bazaar</span> of the Future
                            </h2>
                            <p className="mt-8 font-display italic text-xl text-[var(--champagne)]/85 leading-relaxed border-l border-[var(--line)] pl-6">
                                "Not just a conference. Not just a trade fair. A human festival of ideas."
                            </p>
                        </Reveal>
                        <Reveal delay={150}>
                            <p className="text-base md:text-lg text-muted-luxe leading-relaxed">
                                A three-day celebration where entrepreneurs, investors, creators, innovators and
                                changemakers come together to explore, exchange and elevate the ideas shaping the
                                future of Bharat and beyond.
                            </p>
                            <p className="mt-6 text-base md:text-lg text-muted-luxe leading-relaxed">
                                Startup MahaBazaar 2027 isn't an event — it's an emotion. A celebration of the courage
                                to dream, the resilience to build, and the vision to transform Bharat into a global
                                innovation powerhouse.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-3">
                                {PILLARS.map((p, i) => (
                                    <span key={p} className="px-4 py-2 border hairline text-[0.6rem] tracking-[0.32em] uppercase"
                                        style={{ color: i % 2 ? "var(--champagne)" : "var(--gold)" }}>
                                        {p}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                    </div>

                    {/* MANIFESTO CHAPTERS */}
                    <div className="mt-24">
                        <Reveal>
                            <span className="eyebrow">The Manifesto</span>
                        </Reveal>
                        <motion.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            className="mt-8 border-t hairline"
                            data-testid="manifesto-chapters"
                        >
                            {MANIFESTO.map((c) => (
                                <motion.div key={c.n} variants={riseItem}
                                    className="chapter-row group grid md:grid-cols-[7rem_13rem_1fr] gap-3 md:gap-10 items-baseline py-8 px-2 border-b hairline">
                                    <span className="chapter-num font-display text-4xl md:text-5xl gold-text">{c.n}</span>
                                    <span className="text-[0.62rem] tracking-[0.42em] uppercase text-muted-luxe">{c.word}</span>
                                    <p className="font-display italic text-xl md:text-3xl text-[var(--ivory)]/85 group-hover:text-[var(--champagne)] transition-colors duration-500 leading-snug">
                                        {c.line}
                                    </p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 04 · WHY MAHABAZAAR */}
            <section className={`${wrap} bg-[#1C060C]/60`}>
                <div className="container-x">
                    <SectionHead eyebrow="02 · Why MahaBazaar" title={<>Six Reasons. <span className="gold-text">One Bazaar.</span></>}
                        sub="Every square foot of the festival is engineered for discovery, connection and consequence." />
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-80px" }}
                        className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                        {WHY_CARDS.map((c) => (
                            <motion.div key={c.n} variants={riseItem}>
                                <div className="card-luxe tilt p-8 h-full group" data-testid={`why-card-${c.title.toLowerCase()}`}>
                                    <div className="card-glow" />
                                    <div className="flex items-start justify-between">
                                        <c.icon size={26} strokeWidth={1.2} className="text-[var(--gold)]" />
                                        <span className="font-display text-4xl gold-text opacity-60 group-hover:opacity-100 transition-opacity">{c.n}</span>
                                    </div>
                                    <h3 className="font-display text-2xl mt-8 text-[var(--ivory)]">{c.title}</h3>
                                    <p className="mt-3 text-sm text-muted-luxe leading-relaxed">{c.text}</p>
                                    <div className="mt-6 h-px w-0 group-hover:w-full transition-all duration-700 bg-gradient-to-r from-[var(--gold)] to-transparent" />
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <Marquee
                reverse
                items={[
                    "Climate & Sustainability", "AI & DeepTech", "HealthTech & BioTech", "FinTech & InsurTech",
                    "AgriTech & FoodTech", "Defence, Space & Manufacturing", "Smart Mobility & EV",
                    "D2C & Retail", "Women-led Enterprises", "Bharat 2047", "State Pavilions", "Incubators & CoEs",
                ]}
            />

            {/* 05/06 · INNOVATION BAZAAR + PAVILIONS */}
            <section className={wrap} id="pavilions-preview">
                <Particles density={25} />
                <GlowOrbs />
                <div className="container-x relative">
                    <SectionHead eyebrow="03 · The Innovation Bazaar" title={<>Where Innovation <span className="gold-text">Comes Alive</span></>}
                        sub="120+ startups across 11 thematic experiences — a digital marketplace of the future, built as a grand Indian bazaar." />
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-60px" }}
                        className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
                    >
                        {PAVILIONS.map((p) => (
                            <motion.div key={p.n} variants={riseItem}>
                                <div className="card-luxe group relative p-6 h-64 flex flex-col justify-between overflow-hidden" data-testid={`pavilion-card-${p.n}`}>
                                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${p.tint}, transparent)` }} />
                                    <div className="card-glow" />
                                    <div className="flex items-center justify-between">
                                        <p.icon size={22} strokeWidth={1.2} style={{ color: p.tint }} />
                                        <span className="font-display text-xl text-[var(--ivory)]/25 group-hover:text-[var(--gold)] transition-colors">{p.n}</span>
                                    </div>
                                    <div>
                                        <h3 className="font-display text-lg leading-snug text-[var(--ivory)]">{p.name}</h3>
                                        <p className="mt-3 text-xs text-muted-luxe leading-relaxed max-h-0 opacity-0 group-hover:max-h-24 group-hover:opacity-100 transition-all duration-500 overflow-hidden">
                                            {p.desc}
                                        </p>
                                        <a href="/experience#pavilions" className="mt-4 inline-flex items-center gap-2 text-[0.58rem] tracking-[0.3em] uppercase text-[var(--gold)] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            Explore Pavilion <ArrowRight size={11} />
                                        </a>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* 07 · STARTUP PODS */}
            <section className={`${wrap} bg-[#1C060C]/60 noise`}>
                <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <span className="eyebrow no-lines">04 · Startup Pods</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            Ideas Have <span className="gold-text">a Home.</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            Compact, plug-and-play startup pods give every venture a stage of its own — designed
                            for story, demo and discovery.
                        </p>
                        <div className="mt-10 grid sm:grid-cols-2 gap-5">
                            {POD_CAPABILITIES.map((c) => (
                                <div key={c.n} className="border-l border-[var(--line)] pl-5 py-1" data-testid={`pod-capability-${c.n}`}>
                                    <span className="font-display gold-text text-xl">{c.n}</span>
                                    <h4 className="text-sm tracking-[0.14em] uppercase mt-2 text-[var(--ivory)]">{c.title}</h4>
                                    <p className="text-xs text-muted-luxe mt-2 leading-relaxed">{c.desc}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-10">
                            <GoldBtn to="/register?type=startup" testid="pods-showcase-btn">Showcase Your Startup</GoldBtn>
                        </div>
                    </Reveal>
                    <Reveal delay={200} className="hidden md:block">
                        <IsoPods />
                    </Reveal>
                </div>
            </section>

            {/* 08 · MAHATALKS */}
            <section className={wrap} id="mahatalks-preview">
                <div className="container-x">
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-14 items-start">
                        <Reveal>
                            <span className="eyebrow no-lines">05 · MahaTalks</span>
                            <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                                The Voices <span className="gold-text">of Bharat</span>
                            </h2>
                            <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                                A three-day programme of dialogues, debates, deep dives and conversations
                                exploring the future of entrepreneurship and innovation.
                            </p>
                            <div className="mt-10">
                                <GoldBtn to="/programme" variant="outline" testid="mahatalks-programme-btn">Explore Programme</GoldBtn>
                            </div>
                        </Reveal>
                        <motion.div
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-60px" }}
                            className="grid sm:grid-cols-2 gap-4"
                        >
                            {MAHATALKS_TOPICS.map((t, i) => (
                                <motion.div key={t} variants={riseItem}>
                                    <div className="card-luxe tilt p-6 h-full group" data-testid={`mahatalk-tile-${i}`}>
                                        <div className="card-glow" />
                                        <span className="font-display text-sm text-[var(--gold)]/70">0{i + 1}</span>
                                        <h3 className="font-display text-xl mt-3 leading-snug text-[var(--ivory)] group-hover:text-[var(--champagne)] transition-colors">
                                            {t}
                                        </h3>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* 09 · PITCHYATRA TEASER */}
            <section className="relative py-28 md:py-40 overflow-hidden noise" style={{ background: "linear-gradient(160deg, #26070F 0%, #1C060C 55%, #140409 100%)" }}>
                <Particles density={35} />
                <div className="container-x text-center relative">
                    <Reveal>
                        <span className="eyebrow">06 · The Showdown</span>
                        <h2 className="font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-none mt-8">
                            PITCH<span className="gold-text italic">YATRA</span> 2027
                        </h2>
                        <p className="mt-5 text-[0.62rem] sm:text-xs tracking-[0.42em] uppercase text-muted-luxe">
                            India's Ultimate Startup Showdown
                        </p>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="mt-14 flex items-center justify-center gap-8 sm:gap-16">
                            {[["120", "Startups"], ["5", "Rounds"], ["3", "Days"]].map(([v, l]) => (
                                <div key={l} className="text-center">
                                    <div className="font-display text-5xl sm:text-7xl gold-text font-semibold">{v}</div>
                                    <div className="mt-2 text-[0.58rem] tracking-[0.34em] uppercase text-muted-luxe">{l}</div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={250}>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            {PITCH_ROUNDS.map((r) => (
                                <span key={r.name} className="px-5 py-2.5 border border-[rgba(201,162,75,0.3)] text-[0.6rem] tracking-[0.3em] uppercase text-[var(--champagne)]">
                                    {r.name}
                                </span>
                            ))}
                        </div>
                        <div className="mt-12">
                            <GoldBtn to="/pitchyatra" testid="pitchyatra-teaser-btn">Enter PitchYatra</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 10 · FOUNDER'S JOURNEY DOME */}
            <section className={wrap} id="dome-preview">
                <div className="container-x text-center">
                    <SectionHead eyebrow="07 · The Founder's Journey Dome" title={<>A 360° Story of <span className="gold-text">Building</span></>}
                        sub="Step inside a projection theatre that tells startup journeys — from the first idea to scale and impact. Because innovation is not just built, it's felt." />
                    <Reveal delay={200} className="mt-14 max-w-4xl mx-auto">
                        <Dome />
                    </Reveal>
                </div>
            </section>

            {/* 11 · INVESTOR ADDA */}
            <section className={`${wrap} bg-[#1C060C]/60`}>
                <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <span className="eyebrow no-lines">08 · Investor Adda</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            Where Conversations Become <span className="gold-text">Collaborations</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            The capital heart of the bazaar — speed meets, roundtables and dialogues that turn
                            conversations into collaborations, and collaborations into capital.
                        </p>
                        <div className="mt-10">
                            <GoldBtn to="/investor-adda" testid="adda-join-btn">Join Investor Adda</GoldBtn>
                        </div>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {ADDA_FEATURES.map((f, i) => (
                            <Reveal key={f.title} delay={i * 60}>
                                <div className="border hairline p-5 h-full hover:border-[var(--line)] hover:bg-[rgba(201,162,75,0.04)] transition-all duration-500">
                                    <f.icon size={18} strokeWidth={1.3} className="text-[var(--gold)]" />
                                    <h4 className="mt-4 text-sm font-medium text-[var(--ivory)] leading-snug">{f.title}</h4>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 12 · IMPACT KSHETRA */}
            <section className={wrap}>
                <div className="container-x text-center">
                    <SectionHead eyebrow="09 · Impact Kshetra" title={<>The <span className="gold-text">Future</span> Pavilion</>}
                        sub="Social and environmental innovation — clean water, waste, rural livelihoods, renewables and inclusive growth — mapped from problem to impact." />
                    <div className="mt-16"><ImpactFlow /></div>
                    <Reveal delay={150}>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            {IMPACT_FOCUS.map((f) => (
                                <span key={f} className="px-4 py-2 border hairline text-[0.6rem] tracking-[0.28em] uppercase text-muted-luxe">
                                    {f}
                                </span>
                            ))}
                        </div>
                        <div className="mt-12">
                            <GoldBtn to="/experience#impact" variant="outline" testid="impact-explore-btn">Explore Impact Kshetra</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 13 · KNOWLEDGE LABS */}
            <section className={`${wrap} bg-[#1C060C]/60 noise`}>
                <div className="container-x">
                    <div className="flex flex-wrap items-end justify-between gap-8">
                        <SectionHead center={false} eyebrow="10 · Knowledge & Learning Labs" title={<>Learn From Those <span className="gold-text">Who Build</span></>}
                            sub="20+ masterclasses and innovation studios led by top experts." />
                        <Reveal><GoldBtn to="/knowledge-labs" variant="outline" testid="labs-explore-btn">All Masterclasses</GoldBtn></Reveal>
                    </div>
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-60px" }}
                        className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-4"
                    >
                        {MASTERCLASSES.slice(0, 4).map((m) => (
                            <motion.div key={m.title} variants={riseItem}>
                                <div className="card-luxe tilt p-6 h-full">
                                    <div className="card-glow" />
                                    <span className="text-[0.55rem] tracking-[0.3em] uppercase text-[var(--gold)]">{m.track}</span>
                                    <h3 className="font-display text-lg mt-3 leading-snug text-[var(--ivory)]">{m.title}</h3>
                                    <p className="mt-4 text-xs text-muted-luxe">{m.date} · {m.time} · {m.level}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* 14 · DIGITAL EXPERIENCE */}
            <section className={wrap}>
                <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal className="order-2 lg:order-1">
                        <PhoneMockup />
                    </Reveal>
                    <Reveal delay={150} className="order-1 lg:order-2">
                        <span className="eyebrow no-lines">11 · Digital Experience</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            The MahaBazaar <span className="gold-text">in Your Pocket</span>
                        </h2>
                        <p className="mt-6 text-muted-luxe text-base md:text-lg leading-relaxed">
                            A conceptual event companion — agenda, discovery, matchmaking and voting, carried
                            with you through the bazaar.
                        </p>
                        <div className="mt-9 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {DIGITAL_FEATURES.map((f) => (
                                <div key={f.title} className="border hairline px-4 py-3.5 hover:border-[var(--line)] transition-colors">
                                    <f.icon size={16} strokeWidth={1.3} className="text-[var(--gold)]" />
                                    <p className="mt-2.5 text-[0.62rem] tracking-[0.18em] uppercase text-[var(--ivory)]/80">{f.title}</p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 15 · SPECIAL ZONES */}
            <section className={`${wrap} bg-[#1C060C]/60`}>
                <div className="container-x">
                    <SectionHead eyebrow="12 · Special Zones" title={<>Five Worlds Inside <span className="gold-text">the Bazaar</span></>} />
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-60px" }}
                        className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 gap-4"
                    >
                        {ZONES.map((z, i) => (
                            <motion.div key={z.name} variants={riseItem}>
                                <div className="card-luxe tilt p-6 h-full group" data-testid={`zone-card-${i}`}>
                                    <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${z.tint}, transparent)` }} />
                                    <div className="card-glow" />
                                    <z.icon size={24} strokeWidth={1.2} style={{ color: z.tint }} />
                                    <h3 className="font-display text-lg mt-5 leading-snug text-[var(--ivory)]">{z.name}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{z.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                    <Reveal className="mt-12 text-center">
                        <GoldBtn to="/special-zones" variant="outline" testid="zones-explore-btn">Explore the Zones</GoldBtn>
                    </Reveal>
                </div>
            </section>

            {/* 16 · EVENING EXPERIENCES */}
            <section className="relative py-28 md:py-40 overflow-hidden noise" style={{ background: "linear-gradient(180deg, #140409 0%, #1B0710 50%, #140409 100%)" }}>
                <Particles density={40} />
                <div className="container-x">
                    <SectionHead eyebrow="13 · Evening Experiences" title={<>When Business Meets <span className="gold-text">Celebration</span></>}
                        sub="As the sun sets, the bazaar transforms — lights, music, flavour and spectacle." />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {EVENINGS.map((e, i) => (
                            <Reveal key={e.name} delay={i * 80}>
                                <div className="card-luxe tilt p-7 h-full" data-testid={`evening-card-${i}`}>
                                    <div className="card-glow" />
                                    <div className="w-11 h-11 arch-top border border-[var(--line)] flex items-center justify-center">
                                        <e.icon size={18} strokeWidth={1.3} className="text-[var(--champagne)]" />
                                    </div>
                                    <h3 className="font-display text-xl mt-6 text-[var(--ivory)]">{e.name}</h3>
                                    <p className="mt-3 text-xs text-muted-luxe leading-relaxed">{e.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 17 · AWARDS TEASER */}
            <section className={wrap}>
                <div className="container-x grid lg:grid-cols-[auto_1fr] gap-14 items-center">
                    <Reveal className="mx-auto"><TrophyGlyph className="w-44 md:w-56 floaty" /></Reveal>
                    <Reveal delay={120}>
                        <span className="eyebrow no-lines">14 · Awards Night · 31 January 2027</span>
                        <h2 className="font-display font-medium text-4xl sm:text-5xl leading-[1.06] mt-6">
                            Recognising the <span className="gold-text">Courage to Build</span>
                        </h2>
                        <div className="mt-8 flex flex-wrap gap-3">
                            {AWARDS.slice(0, 6).map((a) => (
                                <span key={a.name} className="px-4 py-2 border hairline text-[0.6rem] tracking-[0.26em] uppercase text-[var(--champagne)]/80">
                                    {a.name}
                                </span>
                            ))}
                        </div>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <GoldBtn to="/awards" variant="outline" testid="awards-explore-btn">Explore the Awards</GoldBtn>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 18 · WHO SHOULD ATTEND */}
            <section className={`${wrap} bg-[#1C060C]/60`}>
                <div className="container-x">
                    <SectionHead eyebrow="15 · Who Should Attend" title={<>The Whole Ecosystem, <span className="gold-text">One Address</span></>} />
                    <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                        {WHO_ATTEND.map((w, i) => (
                            <Reveal key={w.label} delay={(i % 5) * 60}>
                                <div className="border hairline p-5 flex items-center gap-3.5 hover:border-[var(--line)] hover:bg-[rgba(201,162,75,0.05)] transition-all duration-500" data-testid={`attendee-${w.label.toLowerCase().replace(/[\s&]+/g, "-")}`}>
                                    <w.icon size={17} strokeWidth={1.3} className="text-[var(--gold)] shrink-0" />
                                    <span className="text-[0.66rem] tracking-[0.16em] uppercase text-[var(--ivory)]/85">{w.label}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 19 · WHY ATTEND */}
            <section className={wrap}>
                <div className="container-x">
                    <SectionHead center={false} eyebrow="16 · Why Attend" title={<>Six Reasons to Be <span className="gold-text">in the Room</span></>} />
                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-60px" }}
                        className="mt-14 divide-y divide-[rgba(201,162,75,0.1)] border-y hairline"
                    >
                        {WHY_ATTEND.map((w) => (
                            <motion.div key={w.n} variants={riseItem}>
                                <div className="group grid md:grid-cols-[6rem_1fr_1.2fr] gap-4 md:gap-10 items-baseline py-7 px-2 hover:bg-[rgba(201,162,75,0.03)] transition-colors duration-500">
                                    <span className="font-display gold-text text-2xl">{w.n}</span>
                                    <h3 className="font-display text-2xl md:text-3xl text-[var(--ivory)] group-hover:text-[var(--champagne)] transition-colors">
                                        {w.title}
                                    </h3>
                                    <p className="text-sm text-muted-luxe leading-relaxed">{w.text}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* 20 · MAHABAZAAR SAATHI CARDS */}
            <SaathiCards />

            {/* 21 · REGISTER / EXHIBIT / PARTNER */}
            <section className={`${wrap} bg-[#1C060C]/60 noise`}>
                <div className="container-x">
                    <SectionHead eyebrow="18 · Take Your Place" title={<>The Bazaar <span className="gold-text">Awaits</span></>} />
                    <div className="mt-16 grid md:grid-cols-3 gap-5">
                        {[
                            { t: "Register", d: "Attendee, startup, investor, mentor or delegate — claim your place inside the festival.", to: "/register", cta: "Register Now", testid: "triad-register" },
                            { t: "Exhibit", d: "Startup pods, pavilions and national showcases — put your venture on the grand floor.", to: "/exhibition", cta: "Exhibit With Us", testid: "triad-exhibit" },
                            { t: "Partner", d: "Strategic, knowledge, technology and experience partnerships at national scale.", to: "/partner", cta: "Become a Partner", testid: "triad-partner" },
                        ].map((c, i) => (
                            <Reveal key={c.t} delay={i * 100}>
                                <div className="card-luxe tilt p-9 h-full text-center flex flex-col">
                                    <div className="card-glow" />
                                    <span className="font-display gold-text text-xl">0{i + 1}</span>
                                    <h3 className="font-display text-3xl mt-4 text-[var(--ivory)]">{c.t}</h3>
                                    <p className="mt-4 text-sm text-muted-luxe leading-relaxed flex-1">{c.d}</p>
                                    <Magnetic strength={10}>
                                        <GoldBtn to={c.to} variant={i === 0 ? "gold" : "outline"} testid={c.testid} className="mt-8 w-full">
                                            {c.cta}
                                        </GoldBtn>
                                    </Magnetic>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* 21 · FINAL BRAND MOMENT */}
            <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden noise"
                style={{ background: "radial-gradient(ellipse 80% 70% at 50% 110%, rgba(92,16,28,0.55), transparent 60%)" }}
                data-testid="final-brand-section">
                <Particles density={55} />
                <GlowOrbs />
                <div className="container-x relative text-center py-28">
                    <Reveal>
                        <p className="text-[0.66rem] sm:text-sm tracking-[0.5em] uppercase text-muted-luxe">
                            This is not just an event.
                        </p>
                    </Reveal>
                    <Reveal delay={200}>
                        <h2 className="font-display font-medium text-4xl sm:text-6xl lg:text-7xl leading-[1.08] mt-8 max-w-5xl mx-auto">
                            It is the <span className="gold-text italic">Bazaar</span> of India's <span className="gold-text">Next Chapter.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={400}>
                        <img src="/emblem.png" alt="Startup MahaBazaar emblem" className="mx-auto w-14 mt-14 opacity-90" />
                        <p className="mt-6 text-[0.6rem] tracking-[0.6em] uppercase text-[var(--gold)]">Startup MahaBazaar 2027</p>
                        <p className="font-display italic text-[var(--champagne)]/85 mt-3">Bazaar of Ideas, Innovation &amp; Impact</p>
                        <p className="text-[0.6rem] tracking-[0.34em] uppercase text-muted-luxe mt-4">
                            {EVENT.dates} · {EVENT.venue}
                        </p>
                        <div className="mt-12">
                            <Magnetic>
                                <GoldBtn to="/register" testid="final-cta-btn">Be Part of the Movement</GoldBtn>
                            </Magnetic>
                        </div>
                    </Reveal>
                </div>
                <div className="absolute bottom-0 inset-x-0 h-40" style={{ background: "linear-gradient(180deg, transparent, rgba(201,162,75,0.06))" }} />
            </section>
        </div>
    );
}
